/**
 * «Αποθήκευση» της σελίδας «Κατάλογος»: το πρόχειρο γίνεται κλήσεις, με σειρά που κρατά τις
 * αναφορές σωστές (πρώτα ό,τι φτιάχνεται — ομάδες, υλικά, κατηγορίες, προϊόντα — μετά ό,τι
 * τα χρησιμοποιεί). Σταματά στο πρώτο σφάλμα· το SavedSoFar λέει τι γράφτηκε ήδη, ώστε το
 * πρόχειρο που μένει (remainingDraft) να μην ξαναφτιάξει τίποτα.
 */
import type { TFunction } from 'i18next';
import { isFeeProduct } from '../../utils/pricing';
import { CatalogApi, errorMessage } from './catalogApi';
import {
  CatRef, GroupRef, IngRef, PayloadRefs, ProductForm, categoryPayload, productPayload, treeKeys,
} from './catalogModel';
import {
  CatalogBase, CatalogDraft, SavedSoFar, baseProduct, emptySaved, nodeOfRef, orderedProducts, parentsOf,
  treeNeedsReorder,
} from './catalogDraft';
import { PanelTab, productChanges, ChangeContext } from './catalogChanges';

/** Οι δημιουργίες της βιβλιοθήκης (Κ2, components/catalog/libraries/api.ts). */
export interface LibraryCreators {
  createOptionGroup: (storeId: number, body: {
    name: string; selection_type: 'single' | 'multiple'; is_required: boolean;
    choices: Array<{ name: string; price_modifier: number; is_default?: boolean }>;
  }) => Promise<{ id: number }>;
  createSizeGroup: (storeId: number, body: { name: string; items: Array<{ name: string; price_modifier: number; is_default?: boolean }> }) => Promise<{ id: number }>;
  createIngredient: (storeId: number, body: { name: string; price?: number; category?: string | null }) => Promise<{ id: number }>;
}

export interface SaveDeps {
  storeId: number;
  api: CatalogApi;
  library: LibraryCreators;
  t: TFunction;
  changeContext: ChangeContext;
  /** p:ID → σειρά ταμείου (display_order) των ήδη συνδεδεμένων ομάδων, στη σειρά του base.links */
  linkOrders: Record<string, number[]>;
}

export interface SaveProblem {
  owner: string;
  title: string;
  message: string;
  tab?: PanelTab;
}

export interface SaveResult {
  saved: SavedSoFar;
  error?: SaveProblem;
}

const sameList = (a: GroupRef[], b: GroupRef[]) => a.length === b.length && a.every((v, i) => String(v) === String(b[i]));

/** Τα πεδία της φόρμας που άλλαξαν (χωρίς μεγέθη/υλικά — αυτά μετράνε ως «πλήρες PUT»). */
function changedFormFields(orig: ProductForm, cur: ProductForm): string[] {
  const keys = Object.keys({ ...orig, ...cur }) as Array<keyof ProductForm>;
  return keys.filter(k => JSON.stringify(orig[k] ?? null) !== JSON.stringify(cur[k] ?? null)).map(String);
}

/* ───────────────────────── Έλεγχος πριν την αποθήκευση ───────────────────────── */

export function validateDraft(draft: CatalogDraft, base: CatalogBase, t: TFunction): SaveProblem | null {
  const allCats = Object.keys(draft.categories);
  for (let i = 0; i < allCats.length; i += 1) {
    const key = allCats[i];
    if (!draft.categories[key].name.trim()) {
      return { owner: key, title: base.categories[key]?.form.name || t('catalog:category.untitled'), message: t('catalog:errors.categoryNameRequired') };
    }
  }
  const keys = Object.keys(draft.products);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const s = draft.products[key];
    const isNew = draft.newProducts.includes(key);
    if (!isNew && !base.byKey[key]) continue;
    if (!isNew && JSON.stringify(s.form) === JSON.stringify(base.forms[key])) continue;
    const title = s.form.name || base.forms[key]?.name || t('catalog:product.untitled');
    if (!s.form.name.trim()) return { owner: key, title, message: t('catalog:errors.productNameRequired'), tab: 'basics' };
    // Η τιμή ζει μόνο στο μέγεθος: μη-τέλος χωρίς μέγεθος δεν έχει τιμή (ο backend το κόβει κι αυτός).
    if (!isFeeProduct(s.form) && s.form.sizes.length === 0) return { owner: key, title, message: t('catalog:errors.sizesRequired'), tab: 'sizes' };
    const names = s.form.sizes.map(z => z.name.trim());
    if (names.some(n => !n)) return { owner: key, title, message: t('catalog:errors.sizeNameRequired'), tab: 'sizes' };
    const dup = names.find((n, idx) => names.indexOf(n) !== idx);
    if (dup) return { owner: key, title, message: t('catalog:errors.sizeNameDuplicate', { name: dup }), tab: 'sizes' };
  }
  return null;
}

/* ───────────────────────── Αποθήκευση ───────────────────────── */

export async function saveDraft(draft: CatalogDraft, base: CatalogBase, deps: SaveDeps): Promise<SaveResult> {
  const { storeId, api, library, t } = deps;
  const saved = emptySaved();
  const fail = (owner: string, title: string, err: unknown, tab?: PanelTab): SaveResult => ({
    saved, error: { owner, title, message: errorMessage(err, t('catalog:errors.saveFailed')), tab },
  });

  const tree = draft.tree || base.tree;
  const parents = parentsOf(tree);
  const catId = (ref: CatRef): number => {
    if (typeof ref === 'number') return ref;
    if (ref.startsWith('c:')) return Number(ref.slice(2));
    return saved.categories[ref] ?? 0;
  };
  const catIdOfKey = (key: string | null): number | null => (key ? catId(key.startsWith('c:') ? Number(key.slice(2)) : key) : null);
  const groupId = (ref: GroupRef): number => (typeof ref === 'number' ? ref : saved.groups[ref]);
  const ingredientId = (ref: IngRef): number => (typeof ref === 'number' ? ref : saved.ingredients[ref]);
  const refs: PayloadRefs = { categoryId: catId, ingredientId };

  // 1 · Νέες ομάδες επιλογών, υλικά και ομάδες μεγεθών (βιβλιοθήκη)
  for (let i = 0; i < draft.newGroups.length; i += 1) {
    const g = draft.newGroups[i];
    try {
      const res = await library.createOptionGroup(storeId, { name: g.name.trim(), selection_type: g.selection_type, is_required: g.is_required, choices: g.choices });
      saved.groups[g.ref] = Number(res.id);
    } catch (e) { return fail('catalog', g.name, e); }
  }
  for (let i = 0; i < draft.newIngredients.length; i += 1) {
    const ing = draft.newIngredients[i];
    try {
      const res = await library.createIngredient(storeId, { name: ing.name.trim(), price: ing.price, category: ing.category || null });
      saved.ingredients[ing.ref] = Number(res.id);
    } catch (e) { return fail('catalog', ing.name, e); }
  }
  for (let i = 0; i < draft.newSizeGroups.length; i += 1) {
    const g = draft.newSizeGroups[i];
    try {
      await library.createSizeGroup(storeId, { name: g.name.trim(), items: g.items });
      saved.sizeGroups.push(g.ref);
    } catch (e) { return fail('catalog', g.name, e); }
  }

  // 2 · Νέες κατηγορίες (οι ρίζες πριν από τα παιδιά τους)
  const reorder = !!draft.tree && treeNeedsReorder(base.tree, draft.tree);
  const siblingsOf = (key: string) => { const p = parents[key]; return p ? tree.children[p] || [] : tree.roots; };
  const newCats = treeKeys(tree).filter(k => draft.newCategories.includes(k));
  for (let i = 0; i < newCats.length; i += 1) {
    const key = newCats[i];
    const form = draft.categories[key];
    const siblings = siblingsOf(key);
    // Χωρίς αλλαγή σειράς: μετά από τις υπάρχουσες (το display_order τους δεν είναι πάντα 0..n).
    const maxExisting = siblings.filter(k => base.categories[k]).reduce((m, k) => Math.max(m, base.categories[k].display_order), -1);
    const newBefore = siblings.slice(0, siblings.indexOf(key)).filter(k => !base.categories[k]).length;
    const order = reorder ? siblings.indexOf(key) : maxExisting + 1 + newBefore;
    try {
      const res = await api.createCategory(storeId, categoryPayload(form, catIdOfKey(parents[key]), order));
      saved.categories[key] = Number(res.id);
      const groups = draft.categoryGroups[key];
      if (groups && groups.length > 0) await api.setCategoryGroups(Number(res.id), groups.map(groupId));
    } catch (e) { return fail(key, form.name, e); }
  }

  // 3 · Αλλαγμένες κατηγορίες (φόρμα + ομάδες νέων προϊόντων)
  const cats = Object.keys(draft.categories).filter(k => base.categories[k]);
  const catGroupKeys = Object.keys(draft.categoryGroups).filter(k => base.categories[k]);
  const touched = Array.from(new Set(cats.concat(catGroupKeys)));
  for (let i = 0; i < touched.length; i += 1) {
    const key = touched[i];
    const orig = base.categories[key];
    const form = draft.categories[key];
    try {
      if (form && JSON.stringify(form) !== JSON.stringify(orig.form)) {
        const siblings = siblingsOf(key);
        await api.updateCategory(storeId, orig.id, categoryPayload(form, catIdOfKey(parents[key] ?? null), reorder ? siblings.indexOf(key) : orig.display_order));
      }
      const groups = draft.categoryGroups[key];
      const was = base.catGroups[key];
      if (groups && (!was || !sameList(groups, was))) await api.setCategoryGroups(orig.id, groups.map(groupId));
      saved.updatedCategories.push(key);
    } catch (e) { return fail(key, form?.name || orig.form.name, e); }
  }

  // 4 · Σειρά / γονείς κατηγοριών
  if (reorder) {
    const items: Array<{ id: number; parent_id: number | null; display_order: number }> = [];
    tree.roots.forEach((k, i) => {
      items.push({ id: catId(k.startsWith('c:') ? Number(k.slice(2)) : k), parent_id: null, display_order: i });
      (tree.children[k] || []).forEach((c, j) => items.push({ id: catId(c.startsWith('c:') ? Number(c.slice(2)) : c), parent_id: catIdOfKey(k), display_order: j }));
    });
    try {
      await api.reorderCategories(storeId, items);
      saved.tree = true;
    } catch (e) { return fail('catalog', t('catalog:changes.categoriesTitle'), e); }
  }

  // 5 · Νέα προϊόντα
  for (let i = 0; i < draft.newProducts.length; i += 1) {
    const key = draft.newProducts[i];
    const s = draft.products[key];
    if (!s) continue;
    try {
      const body = { ...productPayload(s.form, refs), option_group_ids: (s.groups || []).map(groupId) };
      const res = await api.createProduct(storeId, body);
      const id = Number(res.product?.id);
      saved.products[key] = id;
      if (s.is_kiosk_recommended) await api.setKioskRecommended(id, true);
    } catch (e) { return fail(key, s.form.name, e, 'basics'); }
  }
  const productId = (key: string) => (key.startsWith('p:') ? Number(key.slice(2)) : saved.products[key]);

  // 6 · Μαζικές: διαθεσιμότητα, ταμείο, μετακίνηση (όσα δεν αλλάζουν τίποτα άλλο στη φόρμα)
  const sets: Array<{ ids: number[]; set: { is_available?: boolean; is_hidden?: boolean; category_id?: number } }> = [];
  const addTo = (set: { is_available?: boolean; is_hidden?: boolean; category_id?: number }, id: number) => {
    const sig = JSON.stringify(set);
    const found = sets.find(x => JSON.stringify(x.set) === sig);
    if (found) found.ids.push(id); else sets.push({ ids: [id], set });
  };
  const fullUpdate = new Set<string>();
  Object.keys(draft.products).forEach(key => {
    const s = draft.products[key];
    const isNew = draft.newProducts.includes(key);
    const orig = isNew ? null : baseProduct(base, key);
    if (!isNew && !orig) return;
    const id = productId(key);
    if (!id) return;
    const was = orig || { is_available: true, is_hidden: false };
    if (s.is_available !== was.is_available) addTo({ is_available: s.is_available }, id);
    if (s.is_hidden !== was.is_hidden) addTo({ is_hidden: s.is_hidden }, id);
    if (!orig) return;
    const fields = changedFormFields(orig.form, s.form);
    const moveOnly = fields.length === 1 && fields[0] === 'category_id' && catId(s.form.category_id) > 0;
    if (moveOnly) addTo({ category_id: catId(s.form.category_id) }, id);
    else if (fields.length > 0) fullUpdate.add(key);
  });
  for (let i = 0; i < sets.length; i += 1) {
    try {
      await api.bulk(storeId, sets[i].ids, sets[i].set);
    } catch (e) { return fail('catalog', t('catalog:bulk.title'), e); }
  }

  // 7 · Αλλαγμένα προϊόντα: φόρμα (PUT όπως το παλιό παράθυρο· μόνο χρώμα → PATCH), ομάδες, kiosk
  const existing = base.products.map(p => `p:${p.id}`).filter(k => draft.products[k]);
  for (let i = 0; i < existing.length; i += 1) {
    const key = existing[i];
    const s = draft.products[key];
    const orig = baseProduct(base, key)!;
    if (productChanges(key, orig, s, deps.changeContext).length === 0) continue;
    const id = productId(key);
    try {
      if (fullUpdate.has(key)) {
        const fields = changedFormFields(orig.form, s.form);
        if (fields.length === 1 && fields[0] === 'color') await api.patchProduct(id, { color: s.form.color });
        else await api.updateProduct(id, productPayload(s.form, refs));
      }
      const links = base.links[key];
      if (s.groups && links && !sameList(s.groups, links)) {
        const target = s.groups.map(groupId);
        const current = links.map(Number);
        const kept = current.filter(g => target.includes(g));
        const orderKept = target.filter(g => current.includes(g));
        if (sameList(kept, orderKept)) {
          for (let j = 0; j < current.length; j += 1) if (!target.includes(current[j])) await api.unlinkGroup(id, current[j]);
          const orders = deps.linkOrders[key] || [];
          let next = current.reduce((m, g, idx) => (target.includes(g) ? Math.max(m, Number(orders[idx]) || 0) : m), -1) + 1;
          for (let j = 0; j < target.length; j += 1) {
            if (!current.includes(target[j])) { await api.linkGroup(id, target[j], next); next += 1; }
          }
        } else {
          // Άλλαξε η σειρά: ξανασύνδεση όλων με τη νέα σειρά (το POST δεν αλλάζει σειρά σε υπάρχουσα σύνδεση).
          for (let j = 0; j < current.length; j += 1) await api.unlinkGroup(id, current[j]);
          for (let j = 0; j < target.length; j += 1) await api.linkGroup(id, target[j], j);
        }
      }
      if (s.is_kiosk_recommended !== orig.is_kiosk_recommended) await api.setKioskRecommended(id, s.is_kiosk_recommended);
      saved.updatedProducts.push(key);
    } catch (e) { return fail(key, s.form.name || orig.form.name, e); }
  }

  // 8 · Σειρά προϊόντων στο ταμείο, ανά κατηγορία
  const members: Record<string, string[]> = {};
  const nodeOf = (key: string) => {
    const s = draft.products[key];
    const form = s ? s.form : base.forms[key];
    return nodeOfRef(form.category_id, base.categories, draft);
  };
  base.products.forEach(p => { const k = `p:${p.id}`; (members[nodeOf(k)] = members[nodeOf(k)] || []).push(k); });
  draft.newProducts.forEach(k => { if (draft.products[k]) (members[nodeOf(k)] = members[nodeOf(k)] || []).push(k); });
  const nodes = Object.keys(members).filter(n => n !== 'none');
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    const final = orderedProducts(draft, base, node, members[node]);
    const was = (base.order[node] || []).filter(k => final.includes(k));
    if (final.length === was.length && final.every((k, idx) => k === was[idx])) continue;
    const category = catId(node.startsWith('c:') ? Number(node.slice(2)) : node);
    if (!category) continue;
    try {
      await api.reorderProducts(storeId, category, final.map(productId).filter(Boolean));
      saved.orders.push(node);
    } catch (e) { return fail('catalog', deps.changeContext.nodeName(node), e); }
  }

  return { saved };
}
